import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { libraryEntitySchemasQueryKeys } from '@/hooks/library/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityExtendedJsonSchema } from '@/types/library/ExtendedSchema'

interface UseEntitySchemasArgs<T> {
  orgId: UUID | ''
  select?: (data: PaginatedResponse<EntityExtendedJsonSchema>) => T
}

export const fetchEntitySchemas = async (
  orgId: UUID | '',
  accessToken: string,
): Promise<PaginatedResponse<EntityExtendedJsonSchema>> => {
  if (!orgId) {
    throw new Error('orgId is required')
  }

  let url = `${API_URL}/api/v1/organizations/${orgId}/entities/schemas`

  const params = new URLSearchParams()
  params.append('entity_types', 'catalog_entry')

  const response = await fetch(`${url}?${params.toString()}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }
  return (await response.json()) as PaginatedResponse<EntityExtendedJsonSchema>
}

export const useEntitySchemas = <T = EntityExtendedJsonSchema[]>({
  orgId,
  select,
}: UseEntitySchemasArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: PaginatedResponse<EntityExtendedJsonSchema>) => {
      if (select) {
        return select(data)
      }
      return data.data as unknown as T
    },
    [select],
  )

  return useQuery({
    queryKey: libraryEntitySchemasQueryKeys.organization({ orgId }),
    queryFn: () => fetchEntitySchemas(orgId, accessToken),
    enabled: !!orgId && !!accessToken,
    staleTime: Infinity,
    select: selectFn,
  })
}

interface UseCatalogEntitySchemasArgs {
  orgId: UUID | ''
}

const selectCatalogEntitySchemas = (
  data: PaginatedResponse<EntityExtendedJsonSchema>,
): EntityExtendedJsonSchema[] => {
  return data.data.filter((s) => s.entity_type === 'catalog_entry')
}

export const useCatalogEntitySchemas = ({
  orgId,
}: UseCatalogEntitySchemasArgs) => {
  return useEntitySchemas({
    orgId,
    select: selectCatalogEntitySchemas,
  })
}

const selectCatalogEntitySchemasCount = (
  data: PaginatedResponse<EntityExtendedJsonSchema>,
): number => {
  return data.data.filter((s) => s.entity_type === 'catalog_entry').length
}

export const useCatalogEntitySchemasCount = ({
  orgId,
}: UseCatalogEntitySchemasArgs) => {
  return useEntitySchemas({
    orgId,
    select: selectCatalogEntitySchemasCount,
  })
}

interface UseEntitySchemaArgs<T> {
  orgId: UUID | ''
  schemaId: UUID | ''
  select?: (data: EntityExtendedJsonSchema) => T
}

export const useEntitySchema = <T = EntityExtendedJsonSchema>({
  orgId,
  schemaId,
  select,
}: UseEntitySchemaArgs<T>) => {
  const selectFn = useCallback(
    (data: PaginatedResponse<EntityExtendedJsonSchema>) => {
      const schema = data.data.find((s) => s.id === schemaId)
      if (!schema) return undefined
      if (select) {
        return select(schema)
      }
      return schema as unknown as T
    },
    [schemaId, select],
  )
  return useEntitySchemas({
    orgId,
    select: selectFn,
  })
}

interface FindLatestInLineageArgs {
  startSchemaId: UUID | ''
  schemas: EntityExtendedJsonSchema[]
}

export const findLatestInLineage = ({
  startSchemaId,
  schemas,
}: FindLatestInLineageArgs): EntityExtendedJsonSchema | undefined => {
  if (!startSchemaId || !schemas) {
    return undefined
  }

  // Build a map of predecessor_schema_id -> schema for quick lookup
  const predecessorMap = new Map<UUID, EntityExtendedJsonSchema>()
  schemas.forEach((schema) => {
    if (schema.predecessor_schema_id) {
      predecessorMap.set(schema.predecessor_schema_id, schema)
    }
  })

  // Start with the given schema and follow the chain forward
  let currentSchema = schemas.find((s) => s.id === startSchemaId)
  if (!currentSchema) return undefined

  // Follow the chain to find the latest version
  while (predecessorMap.has(currentSchema.id)) {
    const nextSchema = predecessorMap.get(currentSchema.id)
    if (nextSchema) {
      currentSchema = nextSchema
    } else {
      break
    }
  }

  return currentSchema
}

interface UseEntitySchemaLatestVersionArgs {
  orgId: UUID | ''
  schemaId: UUID | ''
}

export const useEntitySchemaLatestVersion = ({
  orgId,
  schemaId,
}: UseEntitySchemaLatestVersionArgs) => {
  const selectEntitySchemaLatestVersion = useCallback(
    (data: PaginatedResponse<EntityExtendedJsonSchema>) => {
      return findLatestInLineage({
        startSchemaId: schemaId,
        schemas: data.data,
      })
    },
    [schemaId],
  )
  return useEntitySchemas({
    orgId,
    select: selectEntitySchemaLatestVersion,
  })
}
